import i18next from 'i18next';
import {
  SurveysReportGeneratorBundleKey,
  SurveysReportGeneratorDataItemKey,
} from 'screens/WidgetGenerator/reportGeneratorComponents/enums';
import '../../../../i18n';
import { AvailableProductsType } from '@zencity/reporting-types';
import { ReportBuilderClientData } from 'screens/WidgetGenerator/reportGeneratorComponents/types';

export interface DataItemDefinitionGenerator {
  products: AvailableProductsType[];
  clientData: ReportBuilderClientData;
}

const WIDGET_LABELS: Record<SurveysReportGeneratorDataItemKey | SurveysReportGeneratorBundleKey, string> = {
  [SurveysReportGeneratorDataItemKey.CLASSIFICATION_TOP_CONCERNS]: i18next.t(
    'widgetGenerator.widgets.classificationTopConcerns',
  ),
  [SurveysReportGeneratorDataItemKey.CLASSIFICATION_TOP_3_CONCERNS_BREAKDOWN]: i18next.t(
    'widgetGenerator.widgets.classificationTop3ConcernsBreakdown',
  ),
  [SurveysReportGeneratorDataItemKey.CLASSIFICATION_TOP_CONCERNS_BY_AREA]: i18next.t(
    'widgetGenerator.widgets.classificationTopConcernsByArea',
  ),
  [SurveysReportGeneratorDataItemKey.OVERALL_SATISFACTION_TEXT]: i18next.t('widgetGenerator.widgets.overallText'),
  [SurveysReportGeneratorDataItemKey.OVERALL_SATISFACTION_CHART]: i18next.t('widgetGenerator.widgets.overallChart'),
  [SurveysReportGeneratorBundleKey.OVERALL_BUNDLE]: i18next.t('widgetGenerator.widgets.overallBundle'),
  [SurveysReportGeneratorDataItemKey.SENTIMENT_BAR_OVERALL_TABLE]: i18next.t(
    'widgetGenerator.widgets.sentimentBarOverallTable.name',
  ),
  [SurveysReportGeneratorDataItemKey.AREAS_SENTIMENT_TABLE]: i18next.t('widgetGenerator.widgets.areas'),
  [SurveysReportGeneratorDataItemKey.QUESTIONNAIRE]: i18next.t('widgetGenerator.widgets.questionnaire.name'),
  [SurveysReportGeneratorDataItemKey.TOPICS_BUBBLE_CHART]: i18next.t('widgetGenerator.widgets.topicsBubbleChart'),
  [SurveysReportGeneratorDataItemKey.SERVICES_BAR_CHART]: i18next.t('widgetGenerator.widgets.servicesBarChart.name'),
  [SurveysReportGeneratorDataItemKey.WORD_CLOUD]: i18next.t('widgetGenerator.widgets.wordCloud'),
  [SurveysReportGeneratorDataItemKey.DEMOGRAPHICS_TABLE]: i18next.t('widgetGenerator.widgets.demographics'),
  [SurveysReportGeneratorDataItemKey.MAINTAIN_AND_FOCUS_BAR_CHARTS]: i18next.t(
    'widgetGenerator.widgets.maintainAndFocus.name',
  ),
  [SurveysReportGeneratorDataItemKey.CROSS_TABS]: i18next.t('widgetGenerator.widgets.crossTabs.name'),
  [SurveysReportGeneratorDataItemKey.SERVICE_SENTIMENT_BAR]: i18next.t(
    'widgetGenerator.widgets.serviceSentimentBar.name',
  ),
  [SurveysReportGeneratorDataItemKey.CHOICES_QUESTION_CHART]: i18next.t(
    'widgetGenerator.widgets.choicesQuestionChart.name',
  ),
  [SurveysReportGeneratorDataItemKey.BENCHMARK_OVERALL_SATISFACTION]: i18next.t(
    'widgetGenerator.widgets.benchmarkOverallSatisfaction.name',
  ),
  [SurveysReportGeneratorDataItemKey.QUESTIONS_BENCHMARK_TABLE]: i18next.t(
    'widgetGenerator.widgets.questionsBenchmarkTable.name',
  ),
  [SurveysReportGeneratorDataItemKey.BENCHMARK_TABLE_LEGEND]: i18next.t(
    'widgetGenerator.widgets.benchmarkTableLegend.name',
  ),
  [SurveysReportGeneratorDataItemKey.SCORES_OVER_TIME_BY_AREA]: i18next.t(
    'widgetGenerator.widgets.scoresOverTimeByArea.name',
  ),
};

const SUPER_ADMIN_ANNOTATION = ' [SA]';

export const SUPER_ADMIN_WIDGETS = [
  SurveysReportGeneratorDataItemKey.SENTIMENT_BAR_OVERALL_TABLE,
  SurveysReportGeneratorDataItemKey.AREAS_SENTIMENT_TABLE,
  SurveysReportGeneratorDataItemKey.TOPICS_BUBBLE_CHART,
  SurveysReportGeneratorDataItemKey.SERVICES_BAR_CHART,
  SurveysReportGeneratorDataItemKey.DEMOGRAPHICS_TABLE,
  SurveysReportGeneratorDataItemKey.MAINTAIN_AND_FOCUS_BAR_CHARTS,
  SurveysReportGeneratorBundleKey.OVERALL_BUNDLE,
  SurveysReportGeneratorDataItemKey.BENCHMARK_OVERALL_SATISFACTION,
  SurveysReportGeneratorDataItemKey.QUESTIONS_BENCHMARK_TABLE,
  SurveysReportGeneratorDataItemKey.BENCHMARK_TABLE_LEGEND,
];

const addSuperAdminAnnotationToLabel = (label: string): string => `${label} ${SUPER_ADMIN_ANNOTATION}`;

// Add super admin annotation to the labels of the widgets that are only available to super admins
const addSuperAdminAnnotationToLabels = (
  widgetLabels: Record<SurveysReportGeneratorDataItemKey | SurveysReportGeneratorBundleKey, string>,
): Record<SurveysReportGeneratorDataItemKey | SurveysReportGeneratorBundleKey, string> => {
  const updatedWidgetLabels: Record<SurveysReportGeneratorDataItemKey | SurveysReportGeneratorBundleKey, string> = {
    ...WIDGET_LABELS,
  };

  Object.entries(widgetLabels).forEach(([key, value]) => {
    const keyAsDataItemLey = key as SurveysReportGeneratorDataItemKey;
    if (SUPER_ADMIN_WIDGETS.includes(keyAsDataItemLey)) {
      updatedWidgetLabels[keyAsDataItemLey] = addSuperAdminAnnotationToLabel(value);
    }
  });
  return updatedWidgetLabels;
};

export const widgetLabels = addSuperAdminAnnotationToLabels(WIDGET_LABELS);

export const productSuffix = (label: string, product: AvailableProductsType): string => `${label} (${product})`;
